<template lang="pug">
v-container
  div(class="CEModuleRenderer")
    dynamic-content.page-title(
      v-if="pageTitle !== null"
      :content="pageTitle"
      :id="snakeMe(pageTitle)")
    v-btn(icon @click="previousPage()"
      v-show="renderedContent !== null").arrowLeft
        v-icon arrow_back
    v-btn(icon @click="nextPage()"
      v-show="renderedContent !== null").arrowRight
        v-icon arrow_forward
    div(v-show="renderedContent !== null").title-split
    dynamic-content.rendered-content(
      v-if="renderedContent"
      :content="renderedContent"
      @editPopup="editPopup").pagecontent
    div(
      v-show="!renderedContent"
      ).watermark
    v-dialog(v-model="dialog" max-width="900px" persistent)
      v-card
        v-card-title
          span.headline {{ editedItem.key }}
        v-card-text
          v-container(grid-list-md)
            v-layout(wrap)
              v-flex(xs12)
                  v-select(
                    v-if="editedItem.tags.includes('Variable') && \
                      !editedItem.possibleValues"
                    :items="['Yes', 'No']"
                    :label="editedItem.placeholder"
                    v-model="editedItem.md"
                  )
                  v-select(
                    v-if="editedItem.tags.includes('Variable') && \
                      editedItem.possibleValues"
                    :items="editedItem.possibleValues"
                    :label="editedItem.placeholder"
                    v-model="editedItem.md"
                  )
                  c-e-content-editor-quill(
                    v-if="!editedItem.tags.includes('Variable')"
                    :content="editedItem.html"
                    v-on:onUpdate="applyTextEdit"
                  )
          v-checkbox(
            v-model="editedItem.groupLocked"
            label="Group Locked"
            style='margin-left: 16px'
            v-if="isGroupAdmin()"
          )
        v-card-actions
          v-spacer
          v-btn(color="blue darken-1" flat @click.native="close") Cancel
          v-btn(color="blue darken-1" flat @click.native="save") Save
</template>

<script>
// import cheerio from 'cheerio';
import TurndownService from 'turndown';
import striptags from 'striptags';
import { mapState, mapGetters } from 'vuex';
import Base from '@/components/views/Base';
import DynamicContent from '@/components/content/DynamicContent';
import CEContentEditorQuill from '@/components/content/CEContentEditorQuill';
import mergeFieldToLowerCase from '@/lib/util';

const nunjucks = require('nunjucks');
const md = require('markdown-it')({
    html: true
});

// NOTE: Turning off autoescaping can be dangerous
nunjucks.configure('./', { autoescape: false });

export default {
    name: 'CEModuleRenderer',
    extends: Base,

    components: {
        DynamicContent,
        CEContentEditorQuill
    },

    props: {
        cemoduleId: { type: Number, required: true },
        mdContent: { type: String, required: true },
        treeContent: { type: Object, required: false },
        applyContext: { type: Boolean, required: true },
        isEndPage: { type: Boolean, required: true }
    },

    data() {
        return {
            // pageTitle: '',
            // renderedContent: '',
            // doneState: 0,
            contentLoad: false,
            dialog: false,
            editedItem: {
                key: '',
                md: '',
                html: '',
                tags: [],
                placeholder: '',
                possibleValues: [],
                groupLocked: false
            },
            defaultItem: {
                key: '',
                md: '',
                html: '',
                tags: [],
                placeholder: '',
                possibleValues: []
            }
        };
    },

    computed: {
        ...mapState({
            pageNameMap: state => state.navigation.pageNameMap,
            client: state => state.client,
            role: state => state.session.role,
            siteType: state => state.session.siteType
        }),

        ...mapGetters({
            contextItemValues: 'clientContext/values',
            contextItems: 'clientContext/items',
            isAllowedTo: 'session/isAllowedTo',
            hasAccess: 'session/hasAccess'
        }),

        mdTree() {
            let mt = null;
            if (this.treeContent) {
                const flattenTreeField = (tree, field, children, accum = []) => {
                    accum.push(tree[field]);

                    if (tree[children] && Array.isArray(tree[children])) {
                        tree[children].forEach(ntree =>
                            flattenTreeField(ntree, field, children, accum));
                    }
                    return accum;
                };

                mt = flattenTreeField(this.treeContent, 'page', 'children');
            }

            return mt;
        },

        pageTitle() {
            if (this.mdContent &&
                Object.keys(this.pageNameMap).length > 0 &&
                this.contextItemValues) {
                const value = this.mdContent;
                // Remove page title and place in own element
                let title = value.indexOf('\n') > 0 ?
                    value.substr(0, value.indexOf('\n')) : value;
                // Remove the markdown hashes and space at beginning of title
                const pos = value.indexOf('#') > 0 ? title.lastIndexOf('#') + 2 : 0;
                if (pos > 0 && pos < title.length) {
                    title = title.substr(pos);
                }

                try {
                    // Render the context into the title
                    title = nunjucks.renderString(
                        md.render(mergeFieldToLowerCase(title)),
                        this.contextItemValues
                    );

                    return title ? striptags(title.trim()) : title;
                } catch (e) {
                    return `${md.render(mergeFieldToLowerCase(title)).trim()}`;
                }
            }
            return null;
        },

        renderedContent() {
            if (this.mdContent &&
                this.cemoduleId &&
                Object.keys(this.pageNameMap).length > 0 &&
                this.contextItemValues) {
                const value = this.mdContent;

                // Transform inter, intra, intra-app and external links to
                // appropriate product links.
                let content = value.indexOf('\n') > 0 ?
                    value.substring(value.indexOf('\n') + 1) : '';
                content = this.parseLinks(content);
                this.checkGroupLockedTooltip();
                if (content.trim().length === 0) {
                    if (this.isEndPage) {
                        content = 'No content is available for this module';
                    } else {
                        this.nextPage();
                    }
                }
                try {
                    const lines = content.split('\n');
                    const constructedLines = [];
                    let ifCount = 0;
                    let endCount = 0;

                    lines.forEach((line) => {
                        const lineIfVars = line.match(/{%\s*.[^{}]+\s*%}/g);
                        if (lineIfVars && lineIfVars.length > 0) {
                            const ifDirectives = lineIfVars[0].match(
                                /in[Dd]irectives(\s+|\s?)[(（].*?[)）]/g
                            );

                            if (ifDirectives && ifDirectives.length > 0) {
                                const cleanIfDirectives = ifDirectives[0]
                                .replace(/[\u2018\u2019]/g, '"')
                                .replace(/[\u201C\u201D]/g, '"');

                                const directives = cleanIfDirectives.match(/"(.*?)"/g);
                                constructedLines.push(
                                    `<p style="color:grey; font-size:14; font-style:italic;"> [--- ${directives[0].substring(1, directives[0].length - 1)} ---]</p>`
                                );

                                ifCount = 0;
                                endCount = 0;
                                return;
                            }

                            lineIfVars.forEach((item) => {
                                if (item.indexOf('{% if ') >= 0 ||
                                    item.indexOf('{%if ') >= 0) {
                                    ifCount++;
                                }

                                if (item.indexOf('{%endif%}') >= 0 ||
                                    item.indexOf('{% endif%}') >= 0 ||
                                    item.indexOf('{%endif %}') >= 0 ||
                                    item.indexOf('{% endif %}') >= 0) {
                                    endCount++;
                                }
                            });

                            if (endCount > ifCount) {
                                const newline = line.replace(/{%\s*.[^{}]+\s*%}/g, '');
                                constructedLines.push(newline);
                                return;
                            }
                        }
                        constructedLines.push(line);
                    });

                    // Convert markdown to html
                    return nunjucks.renderString(
                        md.render(mergeFieldToLowerCase(constructedLines.join('\n').trim())),
                        this.contextItemValues
                    ).trim();
                } catch (e) {
                    console.log(e);
                    // Try rendering
                    return `<h3>There is a problem on this page.</h3>
                    <p>The content will have errors. Please report to your administrator immediately.</p>
                    <div style="border-style: solid; border-color: var(--v-secondary-base);">
                    <h3>----Raw page data for reference only----</h3>
                    ${content.replace(/{{/g, '<span v-pre>{{').replace(/}}/g, '}}</span>').trim()}
                    </div>`;
                }

                // this.renderedContent = this.parseCheerioLinks(this.renderedContent);
            }
            return null;
        },


    },

    watch: {
        renderedContent(value) {
            // FIXME: This does a second pass to fix links
            // in Commonmark HTML tables using DOM manipulation.
            // More robust for finding all links but doesn't support Vue
            // router-link so inter/intra-module links fully reload pages.
            // Gross. We should move away from commonmark, html tables?
            if (value) {
                this.contentLoad = true;
                setTimeout(() => {
                    // Check for inter-module link
                    const interModuleLinkRegex = /^https?:\/\/ce-viewer.com/;

                    // Check for external link
                    // If external link then leave as is - but add target="_blank"
                    // so it opens in a new tab/window.

                    /* eslint-disable no-useless-escape */
                    const urlRegex = new RegExp([
                        '^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?',
                        '[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?',
                        '(\/.*)?$'
                    ].join(''));
                    /* eslint-disable no-useless-escape */

                    // Check for intra-module link - links within the same module
                    // NOTE: This test is executed last as this regex can also be
                    // triggered by the inter module link regex.
                    const intraModuleLinkRegex = /^#/;

                    const elem = document.querySelector('.rendered-content');
                    const links = [];
                    function walk(node, callback) {
                        callback(node);
                        if (node && node.childNodes) {
                            for (let i = 0; i < node.childNodes.length; i++) {
                                walk(node.childNodes[i], callback);
                            }
                        }
                    }
                    walk(elem, (n) => {
                        if (n &&
                            n.nodeName &&
                            n.nodeName.toLowerCase() === 'a')links.push(n);
                    });
                    links.forEach((cele, index) => {
                        const href = cele.getAttribute('href');
                        const label = cele.innerHTML;

                        const match = interModuleLinkRegex.exec(href);
                        if (match && match[0]) {
                            const link = href.substring(match[0].length);
                            /* eslint-disable no-param-reassign */
                            cele.outerHTML = `<a href="${link}">${label}</a>`;
                        } else if (urlRegex.test(href)) {
                            /* eslint-disable no-param-reassign */
                            cele.outerHTML = `<a href="${href}" target="_blank">${label}</a>`;
                        } else if (intraModuleLinkRegex.test(href)) {
                            // TODO - this markdown decoding might not be enough
                            // Replace all %20 with spaces
                            let pageName = href.substring(1).replace(/%20/g, ' ');

                            // Newer versions of MS Word use dashes instead of %20 to
                            // denote spaces. Replace them as well.
                            pageName = pageName.substring(0).replace(/-/g, ' ');
                            const pagePath =
                                this.pageNameMap[this.cemoduleId][pageName];

                            // Use the module Id from the router parameters
                            const link = `/module/${this.cemoduleId}/page/${pagePath}`;
                            /* eslint-disable no-param-reassign */
                            cele.outerHTML = `<a href="${link}">${label}</a>`;
                        }
                    });
                }, 0);
            }
        },
    },

    methods: {
        // Version of render for document tree
        // Later we might want to load an entire subtree rather than single page
        // renderTree() {
        //     this.renderedContent = '';
        //     let count = 0;
        //     this.mdTree.forEach((value) => {
        //         let content = value;
        //
        //         if (count === 0) {
        //             // Remove page title and place in own element
        //             let title = value.substr(0, value.indexOf('\n'));
        //             // Remove the markdown hashes and space at beginning of title
        //             const pos = title.lastIndexOf('#') + 2;
        //             if (pos > 0 && pos < title.length) {
        //                 title = title.substr(pos);
        //             }
        //
        //             // Render the context into the title
        //             title = nunjucks.renderString(
        //                 md.render(mergeFieldToLowerCase(title)),
        //                 this.contextItemValues
        //             );
        //
        //             this.pageTitle = striptags(title.trim());
        //
        //             // Transform inter, intra, intra-app and external links to
        //             // appropriate product links.
        //             content = value.substring(value.indexOf('\n') + 1);
        //         }
        //         content = this.parseLinks(content);
        //
        //         const ns = nunjucks.renderString(
        //             md.render(mergeFieldToLowerCase(content)),
        //             this.contextItemValues
        //         ).trim();
        //
        //         // ns = this.parseCheerioLinks(ns);
        //
        //         // Convert markdown to html
        //         this.renderedContent += `
        //             <div id="${`page_${count}`}">
        //             ${ns}
        //             </div>
        //             `;
        //         ++count;
        //     });
        // },

        isGroupAdmin() {
            const roleId = parseInt(this.role, 0);
            if ((roleId === 3 || roleId === 4) &&
                this.siteType === 3 &&
                (this.editedItem.tags.includes('Variable') ||
                this.editedItem.tags.includes('Merge Field'))
            ) {
                return true;
            }
            return false;
        },

        snakeMe(title) {
            return title ? title.trim().toLowerCase().replace(/\s+/g, '-') : title;
        },

        // NOTE: Uses regex, not a great way to parse HTML/md
        // Doesn't support html table links. Migrate to GHF markdown, tables?
        parseLinks(page) {
            // const regex = /(?:__|[*#])|\[(.*?)\]\((.*?)\)/gm;
            // const markdownLinkRegex = /(?!!*$)\[(.*?)\]\((.*?)\)/gm;
            const markdownLinkRegex = /\[([^[\]]+)\]\(([^)]+)\)/gm;

            const replacer = (str, label, href, offset, s) => {
                // Check for inter-module link
                const interModuleLinkRegex = /^https?:\/\/ce-viewer.com/;
                const match = interModuleLinkRegex.exec(href);
                if (match && match[0]) {
                    const link = href.substring(match[0].length);
                    return `<router-link to="${link}">${label}</router-link>`;
                }

                // Check for external link
                // If external link then leave as is - but add target="_blank"
                // so it opens in a new tab/window.

                /* eslint-disable no-useless-escape */
                const urlRegex = new RegExp([
                    '^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?',
                    '[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?',
                    '(\/.*)?$'
                ].join(''));
                /* eslint-disable no-useless-escape */

                if (urlRegex.test(href)) {
                    return `<a href="${href}" target="_blank">${label}</a>`;
                }

                // Check for intra-module link - links within the same module
                // NOTE: This test is executed last as this regex can also be
                // triggered by the inter module link regex.
                const intraModuleLinkRegex = /^#/;

                if (intraModuleLinkRegex.test(href)) {
                    // TODO - this markdown decoding might not be enough
                    // Replace all %20 with spaces
                    let pageName = href.substring(1).replace(/%20/g, ' ');

                    // Newer versions of MS Word use dashes instead of %20 to
                    // denote spaces. Replace them as well.
                    pageName = pageName.substring(0).replace(/-/g, ' ');
                    let pagePath = '';
                    const modulePageNames = this.pageNameMap[this.cemoduleId];
                    Object.keys(modulePageNames).forEach((key) => {
                        if (key === pageName) {
                            pagePath = modulePageNames[pageName];
                        }

                        if (pagePath === '') {
                            const newKey = key.replace(/’|'|,/g, '');
                            if (newKey === pageName) {
                                pagePath = modulePageNames[key];
                            }
                        }
                    });

                    // Use the module Id from the router parameters
                    const link = `/module/${this.cemoduleId}/page/${pagePath}`;
                    return `<router-link to="${link}">${label}</router-link>`;
                }

                // If all else fails return the original string
                return str;
            };
            return page.replace(markdownLinkRegex, replacer);
        },

        // Could use cheerio to parse HTML but heavy, DOM redundant on client?
        /*
        parseCheerioLinks(page) {
            const c = cheerio.load(page);
            const links = c('a');

            // Check for inter-module link
            const interModuleLinkRegex = /^https?:\/\/ce-viewer.com/;

            // Check for external link
            // If external link then leave as is - but add target="_blank"
            // so it opens in a new tab/window.

            // eslint-disable no-useless-escape
            const urlRegex = new RegExp([
                '^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?',
                '[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?',
                '(\/.*)?$'
            ].join(''));
            // eslint-disable no-useless-escape

            // Check for intra-module link - links within the same module
            // NOTE: This test is executed last as this regex can also be
            // triggered by the inter module link regex.
            const intraModuleLinkRegex = /^#/;

            for (let i = 0; i < links.length; i++) {
                const cele = links.eq(i);
                const href = cele.attr('href');
                const label = cele.contents();

                if (interModuleLinkRegex.test(href)) {
                    const link = href.substring(20);
                    cele.replaceWith(`<router-link to="${link}">${label}</router-link>`);
                } else if (urlRegex.test(href)) {
                    cele.replaceWith(`<a href="${href}" target="_blank">${label}</a>`);
                } else if (intraModuleLinkRegex.test(href)) {
                    // TODO - this markdown decoding might not be enough
                    // Replace all %20 with spaces
                    let pageName = href.substring(1).replace(/%20/g, ' ');

                    // Newer versions of MS Word use dashes instead of %20 to
                    // denote spaces. Replace them as well.
                    pageName = pageName.substring(0).replace(/-/g, ' ');
                    const pagePath =
                        this.pageNameMap[this.cemoduleId][pageName];

                    // Use the module Id from the router parameters
                    const link = `/module/${this.cemoduleId}/page/${pagePath}`;
                    cele.replaceWith(`<router-link to="${link}">${label}</router-link>`);
                }
            }
            return c.html();
        }
        */
        previousPage() {
            this.$emit('previous');
        },
        nextPage() {
            this.$emit('next');
        },

        editPopup(key) {
            const item = this.contextItems.filter(
                item => item.key.toLowerCase() === key.toLowerCase())[0];
            // child can't edit it
            if (this.isGroupLocked(item)) {
                return;
            }
            if (this.hasAccess('singlecontexteditTable')) {
                this.editedItem.key = item.key;
                this.editedItem.html = item.valueHtml && item.valueHtml !== '' ? item.valueHtml : this.mdToHtml(item.value);
                this.editedItem.md = item.value;
                this.editedItem.tags = item.tags;
                this.editedItem.placeholder = item.placeholder ?
                    item.placeholder : 'Value';
                this.editedItem.possibleValues = item.possibleValues;
                this.editedItem.groupLocked = !!item.ownerId;
                this.dialog = true;
            }
        },

        isGroupLocked(item) {
            function groupLockedByChild(client, ownerId) {
                if (client.children && client.children.length > 0) {
                    return client.children.findIndex((child) => {
                        return child.id === ownerId;
                    }) > -1;
                }
                return false;
            }

            if (item.ownerId) {
                if (this.client && (this.client.id === item.ownerId ||
                    groupLockedByChild(this.client, item.ownerId))) {
                    return false;
                }
                return true;
            }
            return false;
        },

        checkGroupLockedTooltip() {
            Object.keys(this.contextItemValues).forEach((key) => {
                const item = this.contextItems.filter(
                    item => item.key.toLowerCase() === key.toLowerCase())[0];
                if (item && item.ownerId) {
                    let itemValue = this.contextItemValues[key];

                    // if group locked context key could editable, remove tooltip
                    if (itemValue &&
                        itemValue.indexOf('title="This value is managed by your group administrator"') > -1 &&
                        !this.isGroupLocked(item)) {
                        itemValue = itemValue.replace('title="This value is managed by your group administrator"', '');
                    }

                    // child site remove group locked context key formatter
                    if (itemValue &&
                        (this.client && this.client.parentId != null) &&
                        (itemValue.indexOf('class="placeholder"') > -1 ||
                        itemValue.indexOf('class="validContext"') > -1)) {
                        itemValue = itemValue.replace('class="placeholder"', '');
                        itemValue = itemValue.replace('class="validContext"', '');
                    }

                    this.contextItemValues[key] = itemValue;
                }
            });
        },

        applyTextEdit(content) {
            this.editedItem.output = content;
        },

        close() {
            this.dialog = false;
            setTimeout(() => {
                this.editedItem = Object.assign({}, this.defaultItem);
            }, 300);
        },

        mdToHtml(content) {
            if (!content) return null;
            return md.render(content);
        },

        async save() {
            this.setLoading(true);

            try {
                let value = null;
                let valueHtml = '';
                if (this.editedItem.tags.includes('Variable')) {
                    value = this.editedItem.md ? this.editedItem.md : '';
                } else {
                    const turndownService = new TurndownService();
                    // No strikethrough or underline in markdown
                    // use html tags instead.
                    turndownService.keep(['s', 'u']);

                    // handle quill editor li element, convert it to md format manually
                    turndownService.addRule('nestedList', {
                        filter: ['li'],
                        replacement(text, content) {
                            const className = content.getAttribute('class');
                            if (className) {
                                const classNameValue = className.match(/ql-indent-[0-9]*/);
                                const number = classNameValue[0].match(/\d+/g)[0];
                                const loopNumber = parseInt(number, 10);
                                if (loopNumber > 0) {
                                    const mdLISpace = new Array(loopNumber + 2).join('  ');
                                    return `\n ${mdLISpace} * ${text}`;
                                }
                            }
                            return `\n * ${text}`;
                        }
                    });
                    value = this.editedItem.output && this.editedItem.output.length > 0
                        ? turndownService.turndown(this.editedItem.output)
                        : '';
                    valueHtml = this.editedItem.output && this.editedItem.output.length > 0
                        ? this.editedItem.output
                        : '';
                }

                // NOTE: have to explicity set details object to strip any
                // Vue observer objects.
                const details = {
                    key: this.editedItem.key,
                    value,
                    groupLocked: this.editedItem.groupLocked,
                    valueHtml
                };
                await this.$store.dispatch(
                    'clientContext/updateContextItem',
                    details
                );
                const { moduleId, pagePath } = this.$route.params;
                if (moduleId !== undefined && pagePath !== undefined) {
                    this.$router.push({
                        name: 'TempPage',
                        params: { moduleId, pagePath }
                    });
                }
            } catch (error) {
                this.showErrorAlert(error.message);
            } finally {
                this.setLoading(false);
                this.close();
            }
        }
    }
};
</script>

//FYI : https://medium.com/@brockreece/scoped-styles-with-v-html-c0f6d2dc5d8e
<style scoped>
.CEModuleRenderer >>> table {
    border-collapse: collapse;
    width: 100%;
    margin-bottom: 16px;
}

.CEModuleRenderer >>> td, div >>> th {
  border: 1px solid #ddd;
  padding: 16px;
  vertical-align: top;
}

.CEModuleRenderer >>> th {
    padding-top: 12px;
    padding-bottom: 12px;
    text-align: left;
    background-color: #f2f2f2;
/*    color: white;*/
}

.CEModuleRenderer >>> tr:nth-child(even){background-color: #f2f2f2;}

.CEModuleRenderer >>> img {
  width: 100%;
  height: auto;
}

/* Kludge for html2canvas used by html2pdf
Source: https://github.com/niklasvh/html2canvas/issues/796#issuecomment-185748809
*/
.container { font-family: Roboto, "Helvetica Neue", sans-serif; }

.title-split {
    border-bottom: 1px solid var(--v-secondary-base);
    margin-bottom: 24px;
}

.watermark {
   opacity:0.5;
   z-index:99;
   color:black;
}

.arrowLeft {
    color: var(--v-secondary-base) !important;
}

.pagecontent {
    margin-left: 45px;
    margin-right: 45px;
}

@media (max-width:768px){
  .pagecontent{
      overflow: auto;
  }
}

.arrowRight {
    color: var(--v-secondary-base) !important;
    float: right;
}
</style>



// WEBPACK FOOTER //
// src/components/content/CEModuleRenderer.vue